import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const SettingsContext = createContext();
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const SettingsProvider = ({ children }) => {
    const [backendSettings, setBackendSettings] = useState({});
    const [userSettings, setUserSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const savedSettings = localStorage.getItem('userSettings');
        if (savedSettings) {
            try {
                setUserSettings(JSON.parse(savedSettings));
            } catch (err) {
                console.error('Failed to parse saved settings:', err);
            }
        }
    }, []);

    const fetchBackendSettings = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_BASE_URL}/api/settings`);
            setBackendSettings(response.data);
        } catch (err) {
            console.error('Failed to fetch backend settings:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBackendSettings();
    }, [fetchBackendSettings]);

    const updateUserSettings = useCallback((newSettings) => {
        setUserSettings((prev) => {
            const updated = { ...prev, ...newSettings };
            localStorage.setItem('userSettings', JSON.stringify(updated));
            return updated;
        });
    }, []);

    const clearUserSettings = useCallback(() => {
        setUserSettings({});
        localStorage.removeItem('userSettings');
    }, []);

    const getUserSetting = useCallback((key, defaultValue = null) => {
        return userSettings[key] !== undefined ? userSettings[key] : defaultValue;
    }, [userSettings]);

    const getBackendSetting = useCallback((key, defaultValue = null) => {
        return backendSettings[key] !== undefined ? backendSettings[key] : defaultValue;
    }, [backendSettings]);

    const syncSettingsWithBackend = useCallback(async (settings) => {
        try {
            const response = await axios.post('/api/user/settings', settings);
            return response.data;
        } catch (err) {
            console.error('Failed to sync settings with backend:', err);
            throw err;
        }
    }, []);

    const loadUserSettingsFromBackend = useCallback(async () => {
        try {
            const response = await axios.get('/api/user/settings');
            if (response.data) {
                setUserSettings(response.data);
                localStorage.setItem('userSettings', JSON.stringify(response.data));
            }
        } catch (err) {
            console.error('Failed to load user settings from backend:', err);
            throw err;
        }
    }, []);

    const value = {
        backendSettings,
        getBackendSetting,
        refreshBackendSettings: fetchBackendSettings,

        userSettings,
        updateUserSettings,
        clearUserSettings,
        getUserSetting,

        syncSettingsWithBackend,
        loadUserSettingsFromBackend,

        loading,
        error,
    };

    return (
        <SettingsContext.Provider value={value}>
            {children}
        </SettingsContext.Provider>
    );
};
